import { ChangeEvent, useEffect, useState } from 'react';
import { Button, Container, Form, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import deleteIcon from '../../assets/DeleteIcon.svg';
import myOnboardingsIcon from '../../assets/MyOnboardingsIcon.svg';
import viewIcon from '../../assets/ViewIcon.svg';
import OnboardingStatusBadge from '../../components/Badges/OnboardingStatusBadge';
import DeleteOnboardingModal from '../../components/DeleteOnboardingModal/DeleteOnboardingModal';
import Spinner from '../../components/Spinner/Spinner';
import { getCommerce, getMyOnboardings } from '../../services/commerce.service';
import { OnboardingListItem } from '../../types/business/response';
import { FormStatusEnum } from '../../types/enums/form-status.enum';
import { triggerToasts } from '../../utils/helpers';
import { pagesPaths } from '../../utils/navigationUtils';
import { continueOnboardingWizard } from '../../utils/onboardingNavigation';
import {
  CONTRACT_SIGNATURE_PENDING_STATUS,
  getEffectiveOnboardingStatus,
  getStatusValue,
} from '../../utils/statusHelpers';

const formatDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' });

function MyOnboardings() {
  const [onboardings, setOnboardings] = useState<OnboardingListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  // The endpoint doesn't expose a page size; it's inferred from the first full page so
  // Pagination can compute the total amount of pages.
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [deletingBusinessId, setDeletingBusinessId] = useState<number | null>(null);
  // search/status are the draft filter inputs; appliedFilters is what the table actually
  // reflects, only updated when the user clicks "Aplicar" or "Restablecer".
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<{ search: string; status: string }>({
    search: '',
    status: '',
  });

  const navigate = useNavigate();
  const hasActiveFilters = !!appliedFilters.search || !!appliedFilters.status;

  const fetchOnboardings = async (
    pageToFetch: number,
    filters: { search: string; status: string } = appliedFilters,
  ) => {
    setIsLoading(true);
    const response = await getMyOnboardings({
      page: pageToFetch,
      search: filters.search || undefined,
      status: filters.status || undefined,
    });
    setIsLoading(false);

    if (!response?.result) {
      return triggerToasts(response);
    }

    setOnboardings(response.data.business);
    setTotalRecords(response.data.totalRecords);
    if (pageToFetch === 1 && response.data.business.length > 0) {
      setPageSize(response.data.business.length);
    }
  };

  useEffect(() => {
    fetchOnboardings(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value);
  const handleStatusChange = (event: ChangeEvent<HTMLSelectElement>) => setStatus(event.target.value);

  const applyFilters = (filters: { search: string; status: string }) => {
    setAppliedFilters(filters);
    setPage(1);
    if (page === 1) {
      fetchOnboardings(1, filters);
    }
  };

  const handleApplyFilters = () => applyFilters({ search, status });

  const handleResetFilters = () => {
    setSearch('');
    setStatus('');
    applyFilters({ search: '', status: '' });
  };

  const handleView = async (onboarding: OnboardingListItem) => {
    setIsNavigating(true);
    const commerceRut = Number(onboarding.rut);
    const commerceResponse = await getCommerce(commerceRut);
    setIsNavigating(false);

    if (commerceResponse.status === 401 || !commerceResponse?.data) {
      return;
    }

    const { state, step, businessData = {} } = commerceResponse.data;

    // "Sin completar" onboardings get their own read-only preview page with a "Continuar alta"
    // confirm step, instead of jumping straight into the wizard like every other status does.
    const effectiveStatus = getEffectiveOnboardingStatus(onboarding.status, onboarding.contracts);
    if (getStatusValue(effectiveStatus) === 'Sin completar') {
      return navigate(pagesPaths.IncompleteOnboarding, {
        state: { commerceRut, businessData, state, step },
      });
    }

    setIsNavigating(true);
    await continueOnboardingWizard(navigate, {
      commerceRut,
      businessData,
      state,
      step,
      from: pagesPaths.MyOnboardings,
    });
    setIsNavigating(false);
  };

  const handleDeleted = () => {
    setDeletingBusinessId(null);
    const isLastItemOnPage = onboardings.length === 1 && page > 1;
    fetchOnboardings(isLastItemOnPage ? page - 1 : page);
    if (isLastItemOnPage) {
      setPage(page - 1);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const nonDeletableStatuses: string[] = [
    FormStatusEnum[FormStatusEnum.FINISHED],
    FormStatusEnum[FormStatusEnum.DOCS_SIGNED],
  ];
  const isDeletable = (status: string) => !nonDeletableStatuses.includes(status);

  return (
    <Container className="my-onboardings">
      <h1 className="mb-5">
        <img src={myOnboardingsIcon} alt="Mis onboardings" className="me-3" />
        Mis onboardings
      </h1>

      <div className="onboardings-filters">
        <Form.Group className="onboardings-filters-search">
          <Form.Label>Buscar</Form.Label>
          <Form.Control
            type="text"
            placeholder="RUT o Razón social"
            value={search}
            onChange={handleSearchChange}
          />
        </Form.Group>
        <Form.Group className="onboardings-filters-status">
          <Form.Label>Estado</Form.Label>
          <Form.Select value={status} onChange={handleStatusChange}>
            <option value="">Seleccionar</option>
            <option value={CONTRACT_SIGNATURE_PENDING_STATUS}>Pendiente de firma</option>
            <option value={FormStatusEnum[FormStatusEnum.FINISHED]}>Finalizado</option>
            <option value={FormStatusEnum[FormStatusEnum.SIGNED]}>Firmado</option>
            <option value={FormStatusEnum[FormStatusEnum.PENDING]}>Sin completar</option>
          </Form.Select>
        </Form.Group>
        <div className="onboardings-filters-actions">
          <Button
            type="button"
            variant="outline-danger"
            className="primary-outlined-button"
            onClick={handleResetFilters}
          >
            Restablecer
          </Button>
          <Button
            type="button"
            variant="outline-danger"
            className="primary-outlined-button"
            onClick={handleApplyFilters}
          >
            Aplicar
          </Button>
        </div>
      </div>

      <Spinner visible={isLoading || isNavigating} />

      {!isLoading && !isNavigating && onboardings.length === 0 && (
        <p className="empty-state">
          {hasActiveFilters
            ? 'No se encontraron onboardings con los filtros aplicados.'
            : 'Todavía no tenés onboardings a tu cargo.'}
        </p>
      )}

      {!isLoading && !isNavigating && onboardings.length > 0 && (
        <>
          <div className="onboardings-table-scroll">
            <div className="onboardings-table">
              <div className="onboardings-table-header">
                <div>RUT</div>
                <div>Razón social</div>
                <div>Fecha de creación</div>
                <div>Última actualización</div>
                <div>Estado</div>
                <div />
              </div>
              {onboardings.map((onboarding: OnboardingListItem) => (
                <div className="onboardings-table-row" key={onboarding.id}>
                  <div>{onboarding.rut}</div>
                  <div>{onboarding.socialReason}</div>
                  <div>{formatDate(onboarding.createdAt)}</div>
                  <div>{formatDate(onboarding.updatedAt)}</div>
                  <div>
                    <OnboardingStatusBadge
                      status={getEffectiveOnboardingStatus(onboarding.status, onboarding.contracts)}
                    />
                  </div>
                  <div className="onboardings-table-actions">
                    <button
                      type="button"
                      className="table-icon-button"
                      aria-label={`Ver onboarding de ${onboarding.socialReason}`}
                      onClick={() => handleView(onboarding)}
                    >
                      <img src={viewIcon} alt="" />
                    </button>
                    {isDeletable(onboarding.status) && (
                      <button
                        type="button"
                        className="table-icon-button"
                        aria-label={`Eliminar onboarding de ${onboarding.socialReason}`}
                        onClick={() => setDeletingBusinessId(onboarding.id)}
                      >
                        <img src={deleteIcon} alt="" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <Pagination className="onboardings-pagination">
              <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber: number) => (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === page}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              ))}
              <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)} />
            </Pagination>
          )}
        </>
      )}

      <DeleteOnboardingModal
        show={!!deletingBusinessId}
        businessId={deletingBusinessId}
        onCancel={() => setDeletingBusinessId(null)}
        onDeleted={handleDeleted}
      />
    </Container>
  );
}

export default MyOnboardings;
